import { capitalize } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import ConfirmationModal from "../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip-portal";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import {
  getCheckInQuestions,
  getCheckInSchedule,
} from "../../../../utils/questionnaire";
import {
  validateCreateIndividualQuestion,
  validateCreateQuestion,
} from "../../../../utils/questionnaire-validations";
import scriptLoader from "../../../../utils/scriptLoader";
import QuestionCreate from "./QuestionCreate";
import QuestionDisplay from "./QuestionDisplay";

const Questionnaire = ({
  athlete,
  origin,
  userToLoadInformation,
  organizationCoach,
  organizationOwner,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const { locals } = useRillContext();

  const { _id: athleteId } = athlete;
  const { assert, flash } = locals;
  const defaultTemplate =
    organizationOwner?.questionnaireTemplates?.defaultTemplate ||
    userToLoadInformation?.questionnaireTemplates?.defaultTemplate ||
    [];

  const referAsClients = locals.activeUser?.referAsClients || false;
  const roleLabel = referAsClients ? t("programs.client") : t("programs.athlete");

  const [questions, setQuestions] = useState([]);

  const [orderedQuestions, setOrderedQuestions] = useState([]);

  const [changeOrder, setChangeOrder] = useState(false);

  const [currentQuestion, setCurrentQuestion] = useState({
    title: "",
    type: "",
    body: {},
  });

  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const [save, setSave] = useState(false);
  const [edit, setEdit] = useState(false);
  const [addNew, setAddNew] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState({});
  const [schedule, setSchedule] = useState(null);
  const [refresh, setRefresh] = useState(false);
  useEffect(() => {
    const athleteQuestionnaire = getCheckInQuestions(
      organizationOwner || userToLoadInformation,
      athlete
    );
    const athleteSchedule = getCheckInSchedule(athlete);
    setQuestions(athleteQuestionnaire);
    setSchedule(athleteSchedule);
  }, [userToLoadInformation, athlete]);

  useEffect(() => {
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          const sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-handle",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const newOrderedQuestions = Array.from(
                  document.querySelectorAll(".item-grabber-container")
                ).map((question) => question.id);

                setOrderedQuestions(newOrderedQuestions);
              },
            }
          );
        }
      );
    }
  }, []);

  /**
   * Save reorder
   */
  const saveReorder = () => {
    if (orderedQuestions.length > 0) {
      let orderedItems = [...questions];
      orderedItems.sort((a, b) => {
        const indexA = orderedQuestions.indexOf(a._id);
        const indexB = orderedQuestions.indexOf(b._id);
        return indexA - indexB;
      });

      submit("/actions/questionnaire/save-athlete-changes", {
        method: "POST",
        body: {
          questions: orderedItems,
          athleteId,
          // no need to pass the organization owner since it only works with
          // the coach's athlete information (not templates or whatever)
          userToRequest: userToLoadInformation._id,
        },
      });
    }

    setChangeOrder(false);
  };

  /**
   * Cancel reorder
   */
  const cancelReorder = () => {
    // window.location.href = organizationCoach
    //   ? `${origin}/athlete-portal/profile?section=questionnaire&athlete=${athlete._id}&organizationCoach=${organizationCoach}`
    //   : `${origin}/athlete-portal/profile?section=questionnaire&athlete=${athlete._id}`;
    setChangeOrder(false);
    setOrderedQuestions([]);
    setRefresh(!refresh);
  };

  /**
   * Edit question
   */
  const onEdit = (id) => {
    const question = questions.find((question) => question._id === id);
    setEditedQuestion(question);
    setEdit(true);
    setSave(true);
    setAddNew(false);
  };

  const handleSaveQuestion = () => {
    const isEditedValid = edit
      ? validateCreateQuestion(editedQuestion, assert, flash)
      : true;
    const isCreateValid = adding
      ? validateCreateQuestion(currentQuestion, assert, flash)
      : true;

    if (isCreateValid && isEditedValid) {
      const newQuestion = {
        ...currentQuestion,
        _id: `${currentQuestion.title}-${questions.length}`,
      };

      const updatedQuestions = edit
        ? getEditedQuestions(editedQuestion, newQuestion, adding)
        : [...questions, newQuestion];

      const newQuestions = updatedQuestions.map((question) => ({
        title: question.title,
        type: question.type,
        body: { ...question.body },
      }));

      if (validateCreateIndividualQuestion(newQuestions, assert, flash)) {
        setQuestions(updatedQuestions);

        submit("/actions/questionnaire/save-athlete-changes", {
          method: "POST",
          body: {
            questions: newQuestions,
            athleteId,
            // no need to pass the organization owner since it only works with
            // the coach's athlete information (not templates or whatever)
            userToRequest: userToLoadInformation._id,
          },
        });

        setEditedQuestion({});
        setCurrentQuestion({ title: "", type: "", body: {} });
        setSave(false);
        setEdit(false);
        setAdding(false);
        setAddNew(true);
      }
    }
  };

  const handleCancel = () => {
    setEditedQuestion({});
    setCurrentQuestion({ title: "", type: "", body: {} });
    setSave(false);
    setEdit(false);
    setAdding(false);
    setAddNew(true);
  };

  const getEditedQuestions = (editedQuestion, newQuestion, isAdding) => {
    const editedQuestions = questions.map((question) =>
      question._id === editedQuestion._id ? editedQuestion : question
    );

    const result = isAdding
      ? [...editedQuestions, { ...newQuestion }]
      : editedQuestions;

    return result;
  };

  const onDelete = (id) => {
    if (confirm(t("questionnaire.deleteQuestionConfirm"))) {
      const updatedQuestions = questions.filter(
        (question) => question._id !== id
      );

      setQuestions(updatedQuestions);

      submit("/actions/questionnaire/save-athlete-changes", {
        method: "POST",
        body: {
          questions: updatedQuestions,
          athleteId,
          isDeleting: true,
          defaultTemplate,
          // no need to pass the organization owner since it only works with
          // the coach's athlete information (not templates or whatever)
          userToRequest: userToLoadInformation._id,
        },
      });
    }
  };

  const handleAddNew = () => {
    setAddNew(false);
    setAdding(true);
    setSave(true);
  };

  const handleUnscheduleQuestionnaire = () => {
    submit(`/actions/questionnaire/remove-schedule`, {
      method: "POST",
      body: { athleteId, userToRequest: userToLoadInformation._id },
    });

    setTimeout(() => {
      setOpenConfirmationModal(false);
    }, 200);
  };

  return (
    <div role="questionnaire-wrapper">
      <header>
        <div>
          <div className="tooltip">
            <h4>{t("questionnaire.title")}</h4>
            <ToolTip
              id={"check-in-tooltip"}
              body={t("questionnaire.tooltipBody")}
              childrenContainerClass="tooltip-coach"
              timeOut={1000}
              position="top"
            >
              <SvgIcon
                alt="info icon"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
          <p className="p5">
            {t("questionnaire.description", { role: roleLabel })}
          </p>

          {!!questions.length && (
            <ScheduleValidationDisplay
              schedule={schedule}
              questions={questions}
              classNames="force-mobile-hidden"
            />
          )}
        </div>
        <HgroupDisplay
          changeOrder={changeOrder}
          edit={edit}
          adding={adding}
          setChangeOrder={setChangeOrder}
          saveReorder={saveReorder}
          cancelReorder={cancelReorder}
          athleteId={athleteId}
          organizationCoach={organizationCoach}
          questions={questions}
        />
        {!!questions.length && (
          <ScheduleValidationDisplay
            schedule={schedule}
            questions={questions}
            classNames="force-only-show-mobile"
          />
        )}
      </header>
      <div className="questionnaire-body">
        <div className="principal-grabber-div">
          {!!questions.length &&
            questions.map((question, index) => (
              <div
                className="item-grabber-container"
                id={question._id}
                key={`${question._id}${refresh}`}
              >
                {question._id === editedQuestion._id && (
                  <QuestionCreate
                    data={editedQuestion}
                    setData={setEditedQuestion}
                    isEdited={true}
                    key={`${question._id}`}
                  />
                )}

                {question._id !== editedQuestion._id && (
                  <QuestionDisplay
                    key={`${question.title}-${index}`}
                    body={question.body}
                    title={question.title}
                    id={question._id}
                    type={question.type}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    disableEdit={changeOrder}
                  />
                )}
                {changeOrder && (
                  <div className="drag-handle">
                    <SvgIcon src="/img/new-icons/menu.svg" alt="grab" />
                  </div>
                )}
              </div>
            ))}
          {adding && (
            <div className="questionnaire-question-container">
              <QuestionCreate
                data={currentQuestion}
                setData={setCurrentQuestion}
              />
            </div>
          )}
          {!questions.length && !adding && (
            <section className="no-questions">
              <h4>{t("questionnaire.noTitle")}</h4>
              <p className="p5">{t("questionnaire.noDescription")}</p>

              <div>
                <a
                  role="button"
                  data-button-type="primary"
                  href="/actions/refresh?modal[type]=questionnaire-templates"
                >
                  {t("questionnaire.assignTemplate")}
                </a>
                <button data-button-type="secondary" onClick={handleAddNew}>
                  {t("questionnaire.createNew")}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>

      {(!!questions.length || adding) && (
        <>
          {/* ----------- FOOTER FOR DESKTOP---------- */}
          <footer className="force-mobile-hidden">
            {addNew && (
              <button data-button-type="outline" onClick={handleAddNew}>
                {t("questionnaire.addNew")}
              </button>
            )}
            {save && (
              <AsideActionsButtons
                handleCancel={handleCancel}
                handleSaveQuestion={handleSaveQuestion}
              />
            )}
            {!addNew ||
              (!save && !!schedule && (
                <aside>
                  <button
                    data-button-type="secondary"
                    data-action="cancel"
                    onClick={() => {
                      setOpenConfirmationModal(true);
                    }}
                  >
                    {t("questionnaire.unschedule")}
                  </button>
                </aside>
              ))}
            {!addNew ||
              (!save && (
                <aside>
                  <a
                    data-button-type="primary"
                    role="button"
                    href="/actions/refresh?modal[type]=questionnaire-schedule"
                  >
                    {schedule ? t("questionnaire.changeSchedule") : t("questionnaire.schedule")}
                  </a>
                  {schedule && (
                    <ScheduleDisplay
                      className={"footer-schedule"}
                      schedule={schedule}
                    />
                  )}
                </aside>
              ))}
          </footer>
          {/* ----------- FOOTER FOR MOBILE---------- */}
          <footer className="force-only-show-mobile">
            {addNew && (
              <div>
                <button data-button-type="outline" onClick={handleAddNew}>
                  {t("questionnaire.addNew")}
                </button>
                {!addNew ||
                  (!save && (
                    <a
                      data-button-type="primary"
                      role="button"
                      href="/actions/refresh?modal[type]=questionnaire-schedule"
                    >
                      {schedule ? t("questionnaire.changeSchedule") : t("questionnaire.schedule")}
                    </a>
                  ))}
              </div>
            )}
            {save && (
              <AsideActionsButtons
                handleCancel={handleCancel}
                handleSaveQuestion={handleSaveQuestion}
              />
            )}
            {!addNew ||
              (!save && schedule && (
                <ScheduleDisplay
                  className={"footer-schedule"}
                  schedule={schedule}
                />
              ))}
          </footer>
        </>
      )}

      <ConfirmationModal
        isOpen={openConfirmationModal}
        title={t("questionnaire.unscheduleTitle")}
        contentText={t("questionnaire.unscheduleConfirm")}
        cancelButtonText={t("common:cancel")}
        cancelButtonAction={(e) => {
          e.stopPropagation();
          setOpenConfirmationModal(false);
        }}
        confirmButtonText={t("common:delete")}
        confirmButtonAction={handleUnscheduleQuestionnaire}
        icon="delete"
      />
    </div>
  );
};

const ScheduleValidationDisplay = ({
  schedule,
  questions,
  classNames = "",
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  return (
    <div className={classNames}>
      {schedule ? (
        <ScheduleDisplay schedule={schedule} />
      ) : (
        questions.length > 0 && (
          <div className="chip warning">
            {t("questionnaire.notScheduledChip")}
          </div>
        )
      )}
    </div>
  );
};

const AsideActionsButtons = ({ handleCancel, handleSaveQuestion }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  return (
    <aside>
      <button
        data-button-type="secondary"
        data-action="cancel"
        onClick={handleCancel}
      >
        {t("common:cancel")}
      </button>
      <button
        data-button-type="primary"
        role="button"
        onClick={handleSaveQuestion}
      >
        {t("common:save")}
      </button>
    </aside>
  );
};

const HgroupDisplay = ({
  changeOrder,
  edit,
  adding,
  setChangeOrder,
  saveReorder,
  cancelReorder,
  athleteId,
  organizationCoach,
  questions,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  return (
    <hgroup>
      {!changeOrder && !!questions.length && (
        <button
          data-button-type="secondary"
          onClick={() => {
            setChangeOrder(!changeOrder);
          }}
          disabled={changeOrder || edit || adding}
        >
          {t("questionnaire.changeOrder")}
        </button>
      )}
      {changeOrder && (
        <button onClick={saveReorder} data-button-type="primary">
          {t("common:save")}
        </button>
      )}
      {changeOrder && (
        <button
          onClick={cancelReorder}
          data-button-type="secondary"
          data-action="cancel"
        >
          {t("common:cancel")}
        </button>
      )}
      {!changeOrder && (
        <a
          className={!questions.length ? "force-w-full" : ""}
          role="button"
          data-button-type="secondary"
          href="/actions/refresh?modal[type]=questionnaire-templates"
        >
          {t("questionnaire.template")}
        </a>
      )}
      {!changeOrder && !!questions.length && (
        <a
          className="mobile-hidden" // Hidding for mobile cause responsive is not yet ready
          data-button-type="primary"
          role="button"
          href={
            organizationCoach
              ? `/athlete-portal/metrics/check-in-summary?date=${moment().format(
                  "YYYYMMDD"
                )}&athlete=${athleteId}&organizationCoach=${organizationCoach}`
              : `/athlete-portal/metrics/check-in-summary?date=${moment().format(
                  "YYYYMMDD"
                )}&athlete=${athleteId}`
          }
        >
          {t("questionnaire.viewReport")}
        </a>
      )}
    </hgroup>
  );
};

const freqKeyMap = {
  daily: "selectedDay",
  weekly: "week",
  biweekly: "twoWeeks",
  monthly: "month",
};

const ScheduleDisplay = ({ schedule }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const frequency = t(`questionnaire.frequency.${freqKeyMap[schedule.frequency]}`);

  if (schedule.frequency === "daily") {
    const { selectedWeeklyDays } = schedule;

    const days = [];
    for (const day in selectedWeeklyDays) {
      if (selectedWeeklyDays[day].active === true) {
        days.push(capitalize(selectedWeeklyDays[day].day));
      }
    }

    return (
      <div className="chip success">
        {t("questionnaire.scheduleDisplay", { days: days.join(", "), frequency })}
      </div>
    );
  }

  return (
    <div className="chip success">
      {t("questionnaire.scheduleDisplay", {
        days: capitalize(schedule.dayOfWeek.slice(0, 3)),
        frequency,
      })}
    </div>
  );
};

module.exports = Questionnaire;
